/**
 * Store Detail - JavaScript functionality for individual store pages
 * Handles store data loading, map integration, and dynamic content
 */

import { LocationsService, escapeHtml, formatTime, formatPhoneNumber, convertRichTextToHtml } from './locations-service.js';
import { createTranslator } from './translator.js';

/**
 * Store Detail Component
 */
class StoreDetail {
  constructor(element) {
    this.element = element;
    this.loadConfiguration();
    this.bindElements();

    // Initialize component
    this.init();
  }

  /**
   * Load configuration from data attributes
   */
  loadConfiguration() {
    // Get store identifier from data attribute or URL (slug or ID)
    this.storeIdentifier = this.element.dataset.storeId || this.getStoreIdentifierFromUrl();

    this.brand = this.element.dataset.brand || 'bakers';
    this.shopDomain = this.element.dataset.shopDomain;
    this.storefrontApiVersion = this.element.dataset.storefrontApiVersion || '2025-10';
    this.storefrontAccessToken = this.element.dataset.storefrontAccessToken;

    // Map provider configuration
    this.mapProviderType = this.element.dataset.mapProvider || 'mapbox';
    this.mapAccessToken = this.element.dataset.mapAccessToken;
    this.mapProvider = null;
    this.markerUrl = this.element.dataset.markerUrl || '';

    this.countryCode = this.element.dataset.countryCode || 'au';
    this.defaultTimezone = this.element.dataset.defaultTimezone || 'UTC';
    this.deliveryIconsBaseUrl = this.element.dataset.deliveryIconsBaseUrl || '';
    // Delivery service logos from theme settings (data attributes are converted to camelCase)
    this.deliveryLogos = {
      'ubereats': this.element.dataset.deliveryLogoUbereats || '',
      'menulog': this.element.dataset.deliveryLogoMenulog || '',
      'doordash': this.element.dataset.deliveryLogoDoordash || '',
      'skipthedishes': this.element.dataset.deliveryLogoSkipthedishes || ''
    };
    this.enableJsonLd = this.element.dataset.enableJsonLd === 'true';

    // Map controls configuration
    this.mapControls = {
      zoom: this.element.dataset.mapControlZoom || 'none',
      fullscreen: this.element.dataset.mapControlFullscreen || 'none'
    };

    // Map gesture configuration
    this.mapGesture = this.element.dataset.mapGesture || 'cooperative';

    // Map gestures configuration
    const gestureMode = this.element.dataset.mapGesture || 'cooperative';
    const gesturesEnabled = gestureMode !== 'none';

    this.mapGestures = {
      zoom: gestureMode,
      doubleClickZoom: gesturesEnabled,
      dragPan: gesturesEnabled,
      keyboard: gesturesEnabled,
      touchZoomRotate: gesturesEnabled
    };

    // Store data
    this.storeData = null;
    this.map = null;
  }

  /**
   * Initialize services (LocationsService and MapProvider)
   */
  async initializeServices() {
    // Initialize translator (empty namespace to support multiple top-level namespaces)
    this.translator = createTranslator('store-detail-translations', '');

    // Initialize locations service with translator
    this.locationsService = new LocationsService({
      shopDomain: this.shopDomain,
      apiVersion: this.storefrontApiVersion,
      accessToken: this.storefrontAccessToken,
      defaultTimezone: this.defaultTimezone,
      translator: this.translator
    });

    // Dynamically import and initialize map provider
    if (this.mapAccessToken) {
      const mapConfig = {
        token: this.mapAccessToken,
        brand: this.brand,
        defaultLat: this.defaultLat || -25.2744,
        defaultLng: this.defaultLng || 133.7751,
        defaultZoom: 15, // Closer zoom for single store
        markerUrl: this.markerUrl,
        controls: this.mapControls,
        gestures: this.mapGestures
      };

      // Dynamic provider import based on configuration
      // Use URLs from data attributes to ensure proper cache busting (includes ?v= parameter)
      if (this.mapProviderType === 'google') {
        const providerUrl = this.element.dataset.googleProviderUrl || './map-provider-google.js';
        const { GoogleMapsProvider } = await import(providerUrl);
        this.mapProvider = new GoogleMapsProvider(mapConfig);
      } else {
        // Default to Mapbox
        const providerUrl = this.element.dataset.mapboxProviderUrl || './map-provider-mapbox.js';
        const { MapboxProvider } = await import(providerUrl);
        this.mapProvider = new MapboxProvider(mapConfig);
      }
    }
  }

  /**
   * Extract store identifier (slug or ID) from URL
   * Supports both:
   * - New format: /pages/bakeries/warriewood-bakery
   * - Legacy format: /pages/store-detail?id=123
   * @returns {string|null} Store slug or ID
   */
  getStoreIdentifierFromUrl() {
    // Try to get from query parameters first (legacy format)
    const urlParams = new URLSearchParams(window.location.search);
    const queryId = urlParams.get('id') || urlParams.get('store_id');

    if (queryId) {
      return queryId;
    }

    // Try to extract from URL path (new format: /pages/bakeries/slug)
    const pathParts = window.location.pathname.split('/').filter(part => part.length > 0);

    // Look for 'bakeries' segment and get the next part as the slug
    const bakeriesIndex = pathParts.indexOf('bakeries');
    if (bakeriesIndex !== -1 && pathParts.length > bakeriesIndex + 1) {
      return pathParts[bakeriesIndex + 1];
    }

    return null;
  }

  /**
   * Bind DOM elements
   */
  bindElements() {
    this.storeName = this.element.querySelector('#store-name');
    
    // For Bakers brand, get both desktop and mobile status elements
    if (this.brand === 'bakers') {
      this.storeStatusDesktop = this.element.querySelector('#store-status-desktop');
      this.storeStatusMobile = this.element.querySelector('#store-status-mobile');
      // Keep this.storeStatus for backward compatibility (points to desktop)
      this.storeStatus = this.storeStatusDesktop;
    } else {
      this.storeStatus = this.element.querySelector('#store-status');
    }
    
    this.phoneLink = this.element.querySelector('#phone-link');
    this.phoneNumber = this.element.querySelector('#phone-number');
    this.addressLink = this.element.querySelector('#address-link');
    this.addressText = this.element.querySelector('#address-text');
    this.deliverySection = this.element.querySelector('#store-delivery');
    this.deliveryServices = this.element.querySelector('#delivery-services');
    this.hoursList = this.element.querySelector('#hours-list');
    this.specialHoursList = this.element.querySelector('#special-hours-list');
    this.specialHoursSection = this.element.querySelector('#store-special-hours');
    this.storeMap = this.element.querySelector('#store-detail-map');
    this.errorState = this.element.querySelector('#error-state');
    this.phoneContact = this.element.querySelector('.phone-contact');
    this.operatorContact = this.element.querySelector('.operator-contact');
    this.operatorName = this.element.querySelector('#operator-name');
    this.storeContainer = this.element.querySelector('.store-detail__container');

    // My Store elements
    this.myStoreBadge = this.element.querySelector('#store-my-store-badge');
    this.makeMyStoreBtn = this.element.querySelector('#make-my-store-btn');

    // Get status badge template
    this.statusBadgeTemplate = this.element.querySelector('template[name="store-status-badge"]');
  }

  /**
   * Initialize store detail functionality
   */
  async init() {
    if (!this.storeIdentifier) {
      this.showErrorState();
      return;
    }

    // Check if static map is being used (COBS + Mapbox only)
    const useStaticMap = this.mapGesture === 'static' && this.brand === 'cobs' && this.mapProviderType === 'mapbox';

    if (useStaticMap) {
      console.log('Static map mode enabled - skipping interactive map initialization');
    }

    try {
      await this.initializeServices();
      await this.loadStoreData();

      // Only initialize interactive map if not using static map
      if (this.mapAccessToken && this.storeData && !useStaticMap) {
        this.initializeMap();
      }

      // Render static map if enabled
      if (useStaticMap && this.storeData) {
        this.renderStaticMap();
        this.setupStaticMapResizeHandler();
      }

      this.renderStoreData();

    } catch (error) {
      console.error('Failed to initialize store detail:', error);
      this.showErrorState();
    }
  }

  /**
   * Load store data from Shopify Storefront API
   * Tries to load by slug first, then falls back to ID
   */
  async loadStoreData() {
    try {
      // Determine if identifier is a slug or ID
      // IDs are numeric or start with 'gid://'
      const isId = /^\d+$/.test(this.storeIdentifier) || this.storeIdentifier.startsWith('gid://');

      if (isId) {
        // Load by ID (legacy support)
        this.storeData = await this.locationsService.getLocationById(this.storeIdentifier);
      } else {
        // Load by slug (new format)
        this.storeData = await this.locationsService.getLocationBySlug(this.storeIdentifier);
      }
    } catch (error) {
      console.error('Failed to load store data:', error);
      throw error;
    }
  }

  /**
   * Initialize map for single store using provider
   */
  async initializeMap() {
    if (!this.mapProvider || !this.storeMap || !this.storeData) return;

    // Use store coordinates for centering
    const latitude = this.storeData.coordinates.latitude;
    const longitude = this.storeData.coordinates.longitude;

    // Initialize map (note: provider initialization is async but returns quickly)
    this.map = await this.mapProvider.initialize(this.storeMap);

    // Center on store location
    this.mapProvider.setCenter(latitude, longitude, 15);

    // Add single store marker
    this.mapProvider.addMarkers([this.storeData]);

    // Show popup for the store
    this.mapProvider.showPopup(this.storeData, [longitude, latitude]);

    // Setup accessibility
    this.setupMapAccessibility();
  }

  /**
   * Render static map image (COBS + Mapbox only)
   * Builds Mapbox Static Images API URL and updates the image src and alt text
   * Now with responsive sizing for optimal mobile and desktop display
   */
  renderStaticMap() {
    const mapContainer = document.querySelector('.map-container--static');
    if (!mapContainer) return;

    const staticMapImage = document.getElementById('static-map-image');
    if (!staticMapImage) return;

    if (!this.storeData) return;

    if (!this.storeData.coordinates || !this.storeData.coordinates.latitude || !this.storeData.coordinates.longitude) {
      console.error('Store coordinates missing for static map');
      return;
    }

    if (!this.mapAccessToken) {
      console.error('Mapbox access token not available');
      return;
    }

    const latitude = this.storeData.coordinates.latitude;
    const longitude = this.storeData.coordinates.longitude;

    // COBS brand burgundy/red marker
    const markerColor = '800029';

    // Detect viewport and determine optimal map parameters
    const isMobile = window.innerWidth < 768;

    // Get responsive map parameters
    const mapParams = this.getResponsiveMapParams(mapContainer, isMobile);

    // Build Mapbox Static Images API URL
    // Format: /styles/v1/mapbox/{style}/static/{overlay}/{lon},{lat},{zoom}/{width}x{height}@2x
    // Note: Mapbox only supports @2x, not @3x
    const staticMapUrl = `https://api.mapbox.com/styles/v1/mapbox/${mapParams.style}/static/${mapParams.pinSize}+${markerColor}(${longitude},${latitude})/${longitude},${latitude},${mapParams.zoom}/${mapParams.width}x${mapParams.height}@2x?access_token=${this.mapAccessToken}&logo=false`;

    // Update image src
    staticMapImage.src = staticMapUrl;

    // Update width/height attributes for proper aspect ratio (CSS will scale)
    staticMapImage.setAttribute('width', mapParams.width);
    staticMapImage.setAttribute('height', mapParams.height);

    // Set proper alt text with store name
    const altText = this.translator.translate('accessibility.store_detail.map_description', {
      name: this.storeData.name
    });
    staticMapImage.alt = altText;
  }

  /**
   * Get responsive map parameters based on viewport size
   * @param {HTMLElement} mapContainer - Map container element with data attributes
   * @param {boolean} isMobile - Whether the viewport is mobile-sized
   * @returns {Object} Map parameters (width, height, zoom, pinSize, style)
   */
  getResponsiveMapParams(mapContainer, isMobile) {
    if (isMobile) {
      // Mobile parameters - optimized for readability on small screens
      const viewportWidth = window.innerWidth;
      const viewportHeight = Math.min(window.innerHeight * 0.4, 400); // Max 40vh or 400px

      // Calculate dimensions (minimum 600x400 to ensure valid request)
      const width = Math.max(Math.floor(viewportWidth * 0.9 * 2), 600);
      const height = Math.max(Math.floor(viewportHeight * 2), 400);

      return {
        // Request image at 2x the display size for retina screens
        width,
        height,
        zoom: 17, // High zoom for larger pin and maximum detail on mobile
        pinSize: 'pin-l', // Large pin (maximum built-in size available)
        style: 'streets-v12' // Use streets-v12 instead of navigation-day-v1 for consistency
      };
    } else {
      // Desktop parameters - use theme settings or defaults
      const zoom = mapContainer.dataset.staticMapZoom || 14;
      const width = mapContainer.dataset.staticMapWidth || 1000;
      const height = mapContainer.dataset.staticMapHeight || 600;

      return {
        width,
        height,
        zoom,
        pinSize: 'pin-m', // Medium pin for desktop
        style: 'streets-v12' // Standard street style
      };
    }
  }

  /**
   * Setup resize handler for static map to handle device rotation and viewport changes
   * Uses debouncing to avoid excessive API calls
   */
  setupStaticMapResizeHandler() {
    let resizeTimeout;
    let lastWidth = window.innerWidth;

    const handleResize = () => {
      // Only re-render if width changed (avoid unnecessary updates on scroll on mobile)
      if (window.innerWidth !== lastWidth) {
        lastWidth = window.innerWidth;
        this.renderStaticMap();
      }
    };

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      // Debounce resize events (wait 300ms after last resize before updating)
      resizeTimeout = setTimeout(handleResize, 300);
    });
  }

  /**
   * Setup map accessibility features
   */
  setupMapAccessibility() {
    const mapContainer = this.storeMap;

    // Add keyboard event handlers
    mapContainer.addEventListener('keydown', (e) => {
      this.handleMapKeydown(e);
    });

    // Add accessible description
    mapContainer.setAttribute('aria-describedby', 'map-description');

    const description = document.createElement('div');
    description.id = 'map-description';
    description.className = 'visually-hidden';
    description.textContent = this.translator.translate('accessibility.store_detail.map_description', {
      name: this.storeData.name
    });
    mapContainer.appendChild(description);
  }

  /**
   * Handle keyboard navigation for map
   */
  handleMapKeydown(e) {
    if (!this.mapProvider) return;

    const panDistance = 50;
    const zoom = this.mapProvider.getZoom();

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        this.mapProvider.panBy([0, -panDistance]);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.mapProvider.panBy([0, panDistance]);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.mapProvider.panBy([-panDistance, 0]);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.mapProvider.panBy([panDistance, 0]);
        break;
      case '+':
      case '=':
        e.preventDefault();
        this.mapProvider.zoomTo(zoom + 1);
        break;
      case '-':
        e.preventDefault();
        this.mapProvider.zoomTo(zoom - 1);
        break;
    }
  }

  /**
   * Render store data in the UI
   */
  renderStoreData() {
    if (!this.storeData) return;

    this.#syncOpeningAlignedStoreData();

    // Update page title
    if (this.storeName) {
      this.storeName.textContent = this.storeData.name;
      document.title = this.translator.translate('store_detail.page_title', {
        name: this.storeData.name
      });
    }

    // Update status
    this.renderStoreStatus();

    // Update contact information
    this.renderContactInfo();

    // Update delivery services
    this.renderDeliveryServices();

    // Update opening hours
    this.renderOpeningHours();

    // Render special opening hours (public holidays)
    this.renderSpecialOpeningHours();

    // Render store description
    this.renderStoreDescription();

    // Generate JSON-LD structured data
    this.generateJsonLd();

    // Show/hide My Store badge and Save button based on saved store
    this.initMyStore();
  }

  /**
   * Render store status using template
   */
  renderStoreStatus() {
    if (!this.statusBadgeTemplate) return;
    
    // For Bakers, check if we have both desktop and mobile elements
    const statusElements = [];
    if (this.brand === 'bakers') {
      if (this.storeStatusDesktop) statusElements.push(this.storeStatusDesktop);
      if (this.storeStatusMobile) statusElements.push(this.storeStatusMobile);
    } else {
      if (this.storeStatus) statusElements.push(this.storeStatus);
    }
    
    if (statusElements.length === 0) return;

    const status = this.storeData.status || {
      isOpen: false,
      statusText: this.translator.translate('location.status.unknown')
    };
    const { isOpen, isTemporarilyClosed, statusText, nextStateChangeData } = status;

    // Update each status element with the same content
    statusElements.forEach(statusElement => {
      // Clone the template
      const templateContent = this.statusBadgeTemplate.content.cloneNode(true);

      // Status badge (Line 1: Open/Closed/Temporarily Closed)
      const statusEl = templateContent.querySelector('[data-store-status]');
      if (statusEl) {
        const displayText = isOpen
          ? this.translator.translate('location.status.open')
          : isTemporarilyClosed
            ? this.translator.translate('location.status.temporarily_closed')
            : this.translator.translate('location.status.closed');
        statusEl.textContent = displayText;
        statusEl.className = isOpen ? 'store-badge store-badge--open' : 'store-badge store-badge--closed';
      }

      // Closure reason (Only shown when temporarily closed with custom reason)
      const closureReasonEl = templateContent.querySelector('[data-store-closure-reason]');
      const hasClosureReason = isTemporarilyClosed && statusText && statusText !== this.translator.translate('location.status.temporarily_closed');

      if (closureReasonEl) {
        if (hasClosureReason) {
          closureReasonEl.textContent = statusText;
          closureReasonEl.hidden = false;
        } else {
          closureReasonEl.hidden = true;
        }
      }

      // Reopens day (Next status change)
      const nextStatusEl = templateContent.querySelector('[data-store-next-status]');
      if (nextStatusEl && nextStateChangeData && nextStateChangeData.key) {
        const translatedText = this.translator.translate(nextStateChangeData.key, nextStateChangeData.args);
        nextStatusEl.textContent = translatedText;
        nextStatusEl.hidden = false;
      } else if (nextStatusEl) {
        nextStatusEl.hidden = true;
      }

      // Clear and append new content
      statusElement.innerHTML = '';
      statusElement.appendChild(templateContent);
    });
  }

  /**
   * Render contact information
   */
  renderContactInfo() {
    // Phone number - Use native Shopify phone from address
    const phone = this.storeData.phone;
    if (phone && this.phoneLink && this.phoneNumber && this.phoneContact) {
      const formattedPhone = formatPhoneNumber(phone, this.countryCode);
      this.phoneLink.href = `tel:${phone}`;
      this.phoneNumber.textContent = formattedPhone;
      this.phoneContact.style.display = 'block';
    }

    // Address
    if (this.addressLink && this.addressText && this.storeData.address) {
      const address = this.storeData.address;

      // For Bakers stores, put apartment/suite before street address
      // For Cobs stores, keep street address first
      const addressParts = this.brand === 'bakers'
        ? [address.address2, address.address1]  // Bakers: Suite first, then street
        : [address.address1, address.address2]; // Cobs: Street first, then suite

      // Use formatted address from Shopify API for both display and Google Maps link
      const fullAddress = [
        ...addressParts,
        `${address.city}, ${address.provinceCode || address.province} ${address.zip}`
      ].filter(Boolean).join(', ');

      // Update Google Maps link
      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
      this.addressLink.href = mapsUrl;

      this.addressText.innerHTML = escapeHtml(fullAddress);
    }

    // Bakery operator
    const operator = this.storeData.metafields?.bakeryOperator;
    if (operator && this.operatorName && this.operatorContact) {
      const operatorLabel = this.translator.translate('store_detail.bakery_operator', { name: operator });
      this.operatorName.textContent = operatorLabel;
      this.operatorContact.hidden = false;
    }
  }

  /**
   * Convert camelCase/PascalCase to Title Case with spaces
   * Example: UberEats -> Uber Eats, DoorDash -> Door Dash, Menulog -> Menulog
   * @param {string} str - Service name to convert
   * @returns {string} Formatted display name
   */
  toTitleCase(str) {
    return str.replace(/([A-Z])/g, (match, letter, index) => {
      // Add space before capital letters, except at the start
      return index > 0 ? ` ${letter}` : letter;
    });
  }

  /**
   * Render delivery services
   */
  renderDeliveryServices() {
    if (!this.deliverySection || !this.deliveryServices) return;

    // Use singular key as per GraphQL query - deliveryService is an object, not an array
    const services = this.storeData.metafields?.deliveryService;
    if (!services || typeof services !== 'object' || Object.keys(services).length === 0) {
      this.deliverySection.style.display = 'none';
      return;
    }

    const timezone = this.storeData.metafields?.timezone || this.defaultTimezone;
    const now = this.locationsService.getCurrentTime(timezone).getTime();

    // Filter for active services with validity checks (using bakery's timezone)
    const activeServices = Object.entries(services).filter(([serviceName, serviceData]) => {
      if (!serviceData || serviceData.status !== 'active') return false;

      // Check validity period (using bakery's timezone)
      if (serviceData.valid_from || serviceData.valid_to) {
        if (serviceData.valid_from) {
          const validFrom = this.locationsService.parseDate(serviceData.valid_from, timezone);
          if (validFrom && validFrom.getTime() > now) return false;
        }
        if (serviceData.valid_to) {
          const validTo = this.locationsService.parseDate(serviceData.valid_to, timezone);
          if (validTo && validTo.getTime() < now) return false;
        }
      }

      return true;
    });

    if (activeServices.length === 0) {
      this.deliverySection.style.display = 'none';
      return;
    }

    // Render service links
    const serviceLinks = activeServices
      .map(([serviceName, serviceData]) => {
        if (!serviceData.link) return '';
        // Get logo URL from theme settings, fallback to base URL pattern
        const serviceKey = serviceName.toLowerCase();
        let iconUrl = this.deliveryLogos[serviceKey];
        if (!iconUrl) {
          // Fallback to base URL pattern if not in theme settings
          // Special handling for SkipTheDishes (uses skip-square instead of skipthedishes)
          let logoName = serviceKey;
          if (serviceKey === 'skipthedishes') {
            logoName = 'skip-square';
          } else {
            // Add -square suffix for COBS brand fallback
            logoName = serviceKey + (this.brand === 'cobs' ? '-square' : '');
          }
          iconUrl = this.deliveryIconsBaseUrl.replace('menulog', logoName);
        }

        // Convert service name to display format (e.g., UberEats -> Uber Eats)
        const displayName = this.toTitleCase(serviceName);

        const ariaLabel = this.translator.translate('accessibility.store_detail.order_via', {
          service: displayName
        });

        return `
          <a href="${escapeHtml(serviceData.link)}"
             class="delivery-service-link"
             target="_blank"
             rel="noopener noreferrer"
             aria-label="${escapeHtml(ariaLabel)}">
            <img src="${escapeHtml(iconUrl)}"
                 alt="${escapeHtml(displayName)}"
                 class="delivery-service-logo delivery-service-${serviceName.toLowerCase()}"
                 loading="lazy">
          </a>
        `;
      })
      .filter(link => link !== '')
      .join('');

    if (serviceLinks) {
      this.deliveryServices.innerHTML = serviceLinks;
      this.deliverySection.style.display = 'block';
    } else {
      this.deliverySection.style.display = 'none';
    }
  }

  /**
   * Resolve `openingHours` + `status` from MF03 trading hours (MF05 closures still
   * apply). The hours table and JSON-LD advertise the bakery's doors; MF04 pickup
   * hours stay confined to the store selector drawer.
   */
  #syncOpeningAlignedStoreData() {
    if (typeof this.locationsService?.refreshOpeningAlignedLocation !== 'function') return;
    const refreshed = this.locationsService.refreshOpeningAlignedLocation(this.storeData);
    if (refreshed && typeof refreshed === 'object') {
      this.storeData = refreshed;
    }
  }

  /**
   * Render opening hours
   */
  renderOpeningHours() {
    if (!this.hoursList) return;

    const timezone = this.storeData.metafields?.timezone || this.defaultTimezone;
    // MF03 trading week + MF05 closures (via #syncOpeningAlignedStoreData) — not legacy opening_hours
    const hours = this.storeData.metafields?.openingHours;

    if (!hours) {
      this.hoursList.innerHTML = `<p>${this.translator.translate('store_detail.hours_not_available')}</p>`;
      return;
    }

    // Update heading with timezone
    const hoursTitle = document.querySelector('.hours-title');
    if (hoursTitle) {
      hoursTitle.textContent = this.translator.translate('store_detail.opening_hours', { timezone });
    }

    // Order days of the week (Monday first)
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const todayInBakeryTz = this.locationsService.getCurrentTime(timezone);
    const today = todayInBakeryTz.toLocaleDateString('en-US', { weekday: 'long' });

    const hoursHTML = dayOrder
      .map(day => {
        const dayData = hours[day];
        if (!dayData) return '';

        const isToday = day === today;
        const dayClass = isToday ? 'hours-day--today' : '';

        let hoursText = '';
        if (dayData.status === 'closed' || (!dayData.open && !dayData.close)) {
          hoursText = this.translator.translate('location.status.closed');
        } else {
          hoursText = `${formatTime(dayData.open)} - ${formatTime(dayData.close)}`;
        }

        return `
          <div class="hours-day ${dayClass}">
            <div class="day-name">${day}</div>
            <div class="day-hours">${hoursText}</div>
          </div>
        `;
      })
      .join('');

    this.hoursList.innerHTML = hoursHTML;
  }

  /**
   * Render special opening hours (public holidays)
   * Shows holidays within 2 weeks, plus any clustered holidays within 1 week of those
   */
  renderSpecialOpeningHours() {
    if (!this.specialHoursList || !this.specialHoursSection) return;

    const holidayHours = this.storeData.metafields?.holidayOpeningHours;
    const timezone = this.storeData.metafields?.timezone || this.defaultTimezone;

    const specialHoursTitle = document.querySelector('.special-hours-title');
    if (specialHoursTitle) {
      specialHoursTitle.textContent = this.translator.translate('store_detail.special_opening_hours', { timezone });
    }

    const mf05HTML = this.#renderUpcomingMf05ClosuresHtml(timezone);

    let holidaysHTML = '';
    if (holidayHours && Object.keys(holidayHours).length > 0) {
    const currentDate = this.locationsService.getCurrentTime(timezone);

    // Calculate today's date at midnight
    const todayMidnight = new Date(currentDate);
    todayMidnight.setHours(0, 0, 0, 0);

    // Calculate date 2 weeks (14 days) from today
    const twoWeeksFromNow = new Date(todayMidnight);
    twoWeeksFromNow.setDate(todayMidnight.getDate() + 14);

    // Pre-parse all future holiday dates
    const parsedHolidays = Object.entries(holidayHours)
      .filter(([holidayName, holidayData]) => holidayData.date)
      .map(([holidayName, holidayData]) => {
        const [year, month, day] = holidayData.date.split('-').map(Number);
        const holidayDate = new Date(year, month - 1, day);
        holidayDate.setHours(0, 0, 0, 0);
        return { holidayName, holidayData, holidayDate };
      })
      .filter(holiday => holiday.holidayDate >= todayMidnight);

    // Phase 1: Find anchor holidays within 2 weeks
    const includedDates = new Set();
    for (const holiday of parsedHolidays) {
      if (holiday.holidayDate <= twoWeeksFromNow) {
        includedDates.add(holiday.holidayData.date);
      }
    }

    // Phase 2: Cascade — include holidays within 1 week of any already-included holiday
    const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (const candidate of parsedHolidays) {
        if (includedDates.has(candidate.holidayData.date)) continue;
        for (const included of parsedHolidays) {
          if (!includedDates.has(included.holidayData.date)) continue;
          if (Math.abs(candidate.holidayDate - included.holidayDate) <= ONE_WEEK_MS) {
            includedDates.add(candidate.holidayData.date);
            expanded = true;
            break;
          }
        }
      }
    }

    // Sort by date and render
    holidaysHTML = parsedHolidays
      .filter(holiday => includedDates.has(holiday.holidayData.date))
      .sort((a, b) => a.holidayDate - b.holidayDate)
      .map(holiday => {
        let hoursText;
        if (holiday.holidayData.status === 'closed' || (!holiday.holidayData.open && !holiday.holidayData.close)) {
          hoursText = this.translator.translate('location.status.closed');
        } else {
          hoursText = `${formatTime(holiday.holidayData.open)} - ${formatTime(holiday.holidayData.close)}`;
        }

        return `
          <div class="special-hours-day">
            <div class="special-day-name">${escapeHtml(holiday.holidayName)}</div>
            <div class="special-day-hours">${hoursText}</div>
          </div>
        `;
      })
      .join('');
    }

    const combined = [mf05HTML, holidaysHTML].filter(Boolean).join('');
    if (combined) {
      this.specialHoursList.innerHTML = combined;
      this.specialHoursSection.hidden = false;
    } else {
      this.specialHoursSection.hidden = true;
    }
  }

  /**
   * Upcoming MF05 closure / reduced-hour dates (next 14 days, bakery timezone).
   * @private
   */
  #renderUpcomingMf05ClosuresHtml(timezone) {
    const closures = this.storeData.metafields?.closureDates;
    if (!Array.isArray(closures) || !closures.length) return '';

    const currentDate = this.locationsService.getCurrentTime(timezone);
    const todayMidnight = new Date(currentDate);
    todayMidnight.setHours(0, 0, 0, 0);
    const twoWeeksFromNow = new Date(todayMidnight);
    twoWeeksFromNow.setDate(todayMidnight.getDate() + 14);

    return closures
      .map((entry) => {
        if (!entry?.date) return null;
        const [year, month, day] = entry.date.split('-').map(Number);
        const closureDate = new Date(year, month - 1, day);
        closureDate.setHours(0, 0, 0, 0);
        return { entry, closureDate };
      })
      .filter((row) => row && row.closureDate >= todayMidnight && row.closureDate <= twoWeeksFromNow)
      .sort((a, b) => a.closureDate - b.closureDate)
      .map(({ entry }) => {
        let hoursText;
        if (entry.isClosed) {
          hoursText = this.translator.translate('location.status.closed');
        } else if (entry.open && entry.close) {
          hoursText = `${formatTime(entry.open)} - ${formatTime(entry.close)}`;
        } else {
          hoursText = this.translator.translate('location.status.closed');
        }
        const label = entry.label || entry.date;
        return `
          <div class="special-hours-day" data-mf05-closure>
            <div class="special-day-name">${escapeHtml(label)}</div>
            <div class="special-day-hours">${hoursText}</div>
          </div>
        `;
      })
      .join('');
  }

  /**
   * Render store description (rich text from metafields)
   */
  renderStoreDescription() {
    const descriptionContainer = document.getElementById('store-description');
    const descriptionTitle = document.getElementById('store-description-title');
    if (!descriptionContainer) return;

    const description = this.storeData.metafields?.description;
    const publicStoreName = this.storeData.metafields?.public_store_name || this.storeData.name;

    // Hide if no description available or if rich text is empty
    if (!description) {
      descriptionContainer.style.display = 'none';
      return;
    }

    // Check if rich text has actual text content (not just empty structure)
    const isRichTextEmpty = (jsonString) => {
      if (!jsonString) return true; // null, undefined, or empty string

      let data;
      try {
        data = JSON.parse(jsonString);
      } catch {
        return true; // invalid JSON
      }

      if (!data || !Array.isArray(data.children)) return true;

      // Recursive check for any non-empty text node
      const hasText = (nodes) =>
        nodes.some((node) => {
          if (node.type === 'text' && node.value?.trim() !== '') return true;
          if (Array.isArray(node.children)) return hasText(node.children);
          return false;
        });

      return !hasText(data.children);
    };

    if (isRichTextEmpty(description)) {
      descriptionContainer.style.display = 'none';
      return;
    }

    // Update heading with brand-specific pattern
    if (descriptionTitle) {
      descriptionTitle.textContent = 'About this bakery';
    }

    try {
      // Convert rich text schema to HTML with scoped class
      const html = convertRichTextToHtml(description, { scoped: 'store-description__content' });

      if (html) {
        // Preserve the h2 title and append description content
        const contentDiv = document.createElement('div');
        contentDiv.innerHTML = html;

        // Clear existing content except the h2
        const heading = descriptionContainer.querySelector('h2');
        descriptionContainer.innerHTML = '';
        if (heading) {
          descriptionContainer.appendChild(heading);
        }
        descriptionContainer.appendChild(contentDiv);
        descriptionContainer.style.display = 'block';
      } else {
        descriptionContainer.style.display = 'none';
      }
    } catch (error) {
      console.error('Failed to render store description:', error);
      descriptionContainer.style.display = 'none';
    }
  }

  /**
   * Generate and inject JSON-LD structured data for LocalBusiness
   */
  generateJsonLd() {
    if (!this.enableJsonLd) return;

    const jsonLdScript = document.getElementById('store-jsonld');
    if (!jsonLdScript || !this.storeData) return;

    const store = this.storeData;
    const address = store.address;
    const openingHours = store.metafields?.openingHours;

    // Build opening hours specification
    const openingHoursSpec = [];
    if (openingHours) {
      const dayMap = {
        0: 'Sunday',
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday'
      };

      // Group days by same opening hours
      const hoursGroups = {};

      Object.entries(openingHours).forEach(([dayIndex, hours]) => {
        if (hours.status === 'open' && hours.open && hours.close) {
          const key = `${hours.open}-${hours.close}`;
          if (!hoursGroups[key]) {
            hoursGroups[key] = {
              opens: hours.open,
              closes: hours.close,
              days: []
            };
          }
          hoursGroups[key].days.push(dayMap[dayIndex]);
        }
      });

      // Convert groups to OpeningHoursSpecification format
      Object.values(hoursGroups).forEach(group => {
        openingHoursSpec.push({
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": group.days.length === 1 ? group.days[0] : group.days,
          "opens": group.opens,
          "closes": group.closes
        });
      });
    }

    // Build the LocalBusiness schema
    // For Bakers stores, put apartment/suite before street address (same as display format)
    const streetAddressParts = this.brand === 'bakers'
      ? [address.address2, address.address1]  // Bakers: Suite first, then street
      : [address.address1, address.address2]; // Cobs: Street first, then suite

    const schema = {
      "@context": "https://schema.org",
      "@type": "Bakery",
      "name": store.name,
      "image": store.metafields?.featuredImage || "",
      "telephone": store.phone || "",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": streetAddressParts.filter(Boolean).join(', '),
        "addressLocality": address.city,
        "addressRegion": address.province,
        "postalCode": address.zip,
        "addressCountry": address.countryCode
      }
    };

    // Add coordinates if available
    if (store.coordinates?.latitude && store.coordinates?.longitude) {
      schema.geo = {
        "@type": "GeoCoordinates",
        "latitude": store.coordinates.latitude,
        "longitude": store.coordinates.longitude
      };
    }

    // Add opening hours if available
    if (openingHoursSpec.length > 0) {
      schema.openingHoursSpecification = openingHoursSpec;
    }

    // Inject the JSON-LD
    jsonLdScript.textContent = JSON.stringify(schema, null, 2);
  }

  // calculateStatus, parseTimeToday, calculateNextOpening, getTodaysHolidayHours,
  // isTimeWithinHours, timeToMinutes, and getBrandPrimaryColor methods removed
  // Now handled by LocationsService and MapboxProvider

  /**
   * Read the currently-saved store ID.
   * Prefers window.StoreSelectionState (shared with the store-selector drawer),
   * falls back to localStorage — matching the same logic used by store-locator.js.
   */
  loadSelectedStoreId() {
    const stateApi = window.StoreSelectionState;
    if (stateApi && typeof stateApi.getSelection === 'function') {
      const selection = stateApi.getSelection();
      if (selection?.storeId) return selection.storeId;
    }
    try {
      return localStorage.getItem('amb_selected_store_id') || null;
    } catch {
      return null;
    }
  }

  /**
   * Toggle badge / button visibility based on whether savedId matches this store.
   * @param {string|null} savedId
   */
  updateMyStoreState(savedId) {
    if (!this.storeData) return;
    const isSelected = !!savedId && this.storeData.id === savedId;
    // MF37: bakeries without Click & Collect can't be saved as "my bakery"
    // (matches the store locator cards; the SSD filters them out entirely).
    const clickCollectEnabled = LocationsService.isClickCollectEnabled(this.storeData);
    if (this.myStoreBadge) this.myStoreBadge.hidden = !isSelected;
    if (this.makeMyStoreBtn) this.makeMyStoreBtn.hidden = isSelected || !clickCollectEnabled;
  }

  /**
   * Initialise "My Store" badge + button for the store detail page.
   * Make-my-store opens SSD with pending selection (save + pickup handled in drawer).
   */
  initMyStore() {
    if (!this.storeData) return;

    this.updateMyStoreState(this.loadSelectedStoreId());

    if (this.makeMyStoreBtn) {
      this.makeMyStoreBtn.dataset.pendingStoreId = this.storeData.id || '';
    }

    // Stay in sync when the selection changes externally (e.g. store-selector drawer)
    const stateApi = window.StoreSelectionState;
    if (stateApi && typeof stateApi.subscribe === 'function') {
      stateApi.subscribe((event) => {
        if (event.type === 'store-selection:changed') {
          this.updateMyStoreState(event.detail?.selection?.storeId || null);
        } else if (event.type === 'store-selection:cleared') {
          this.updateMyStoreState(null);
        }
      });
    }
  }

  /**
   * Show error state
   */
  showErrorState() {
    // Hide the main store container
    if (this.storeContainer) {
      this.storeContainer.hidden = true;
    }

    // Show error state
    if (this.errorState) {
      this.errorState.hidden = false;
    }

    // Update page title
    document.title = this.translator.translate('store_detail.page_title_not_found');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const storeDetailElement = document.querySelector('.store-detail');
  if (storeDetailElement) {
    new StoreDetail(storeDetailElement);
  }
});
